import React, { useState } from "react";
import { tWithKeyAttr as t } from "../../../../i18nHelper";
import styles from "../DeviceConfigForm.module.css";
import {
  DeviceCategory,
  DeviceConfig,
  ElectrodePolarity,
} from "../../../../services/types/device.types";
import {
  DEFAULT_DURATION_MINUTES,
  DEFAULT_INTENSITY_MA,
  ELECTRODE_POLARITIES,
  MAX_INTENSITY_MA,
  STIMULATION_ELECTRODE_COUNT,
  setStimulationPolarity,
  supportsFrequency,
  toggleStimulationSite,
} from "../../../../utils/deviceConfiguration";
import ElectrodeGrid from "./ElectrodeGrid";

interface StimulationPanelProps {
  category: DeviceCategory;
  config: DeviceConfig;
  onChange: (patch: Partial<DeviceConfig>) => void;
}

function StimulationPanel({ category, config, onChange }: StimulationPanelProps) {
  const electrodes = config.electrodes ?? {};
  const selectedSites = Object.keys(electrodes);
  const [openSite, setOpenSite] = useState<string | null>(null);

  const handleSiteClick = (site: string) => {
    if (electrodes[site]) {
      setOpenSite(openSite === site ? null : site);
      return;
    }
    onChange({ electrodes: toggleStimulationSite(electrodes, site) });
    setOpenSite(site);
  };

  const handlePolarity = (site: string, polarity: ElectrodePolarity) => {
    onChange({ electrodes: setStimulationPolarity(electrodes, site, polarity) });
  };

  const handleRemove = (site: string) => {
    onChange({ electrodes: toggleStimulationSite(electrodes, site) });
    setOpenSite(null);
  };

  return (
    <>
      <div className={styles.field}>
        <label className={styles.label}>
          {t("DEVICE_ELECTRODES_POLARITY")} <span className={styles.req}>*</span>
          <span className={styles.counter}>
            {selectedSites.length}/{STIMULATION_ELECTRODE_COUNT}
          </span>
        </label>
        <ElectrodeGrid
          stateOf={(site) => electrodes[site] ?? "unused"}
          onSiteClick={handleSiteClick}
          openSite={openSite}
          disabledSite={(site) =>
            !electrodes[site] && selectedSites.length >= STIMULATION_ELECTRODE_COUNT
          }
        />
        <div className={styles.legend}>
          <span>
            <i className={`${styles.legendDot} ${styles.legend_anode}`} />
            {t("DEVICE_ANODE")}
          </span>
          <span>
            <i className={`${styles.legendDot} ${styles.legend_cathode}`} />
            {t("DEVICE_CATHODE")}
          </span>
          <span>
            <i className={`${styles.legendDot} ${styles.legend_unused}`} />
            {t("DEVICE_UNUSED")}
          </span>
        </div>
        <p className={styles.hintText}>{t("DEVICE_STIM_ELECTRODE_HINT")}</p>
      </div>

      {openSite && electrodes[openSite] && (
        <div className={styles.fanPanel}>
          <div className={styles.fanHeader}>
            <span className={styles.fanTitle}>{openSite}</span>
            <button
              type="button"
              className={styles.fanRemove}
              onClick={() => handleRemove(openSite)}
            >
              {t("REMOVE")}
            </button>
          </div>
          <div className={styles.modeToggle}>
            {ELECTRODE_POLARITIES.map((p) => (
              <button
                key={p.value}
                type="button"
                className={`${styles.modeBtn} ${
                  electrodes[openSite] === p.value ? styles[`modeBtn_${p.value}`] : ""
                }`}
                onClick={() => handlePolarity(openSite, p.value)}
              >
                {t(p.labelKey)}
              </button>
            ))}
          </div>
        </div>
      )}

      <div className={styles.row}>
        <div className={styles.field}>
          <label className={styles.label}>
            {t("DEVICE_INTENSITY_MA")} <span className={styles.req}>*</span>
          </label>
          <input
            type="number"
            min={0}
            max={MAX_INTENSITY_MA}
            step="0.1"
            className={styles.input}
            value={config.intensityMa ?? DEFAULT_INTENSITY_MA}
            onChange={(e) =>
              onChange({ intensityMa: e.target.value === "" ? undefined : Number(e.target.value) })
            }
          />
        </div>

        {supportsFrequency(category) && (
          <div className={styles.field}>
            <label className={styles.label}>
              {t("DEVICE_FREQUENCY_HZ")} <span className={styles.req}>*</span>
            </label>
            <input
              type="number"
              min={0}
              step="any"
              className={styles.input}
              value={config.frequencyHz ?? ""}
              onChange={(e) =>
                onChange({ frequencyHz: e.target.value === "" ? undefined : Number(e.target.value) })
              }
            />
          </div>
        )}

        <div className={styles.field}>
          <label className={styles.label}>
            {t("DEVICE_DURATION_MINUTES")} <span className={styles.req}>*</span>
          </label>
          <input
            type="number"
            min={1}
            className={styles.input}
            value={config.durationMinutes ?? DEFAULT_DURATION_MINUTES}
            onChange={(e) =>
              onChange({ durationMinutes: e.target.value ? Number(e.target.value) : undefined })
            }
          />
        </div>
      </div>

      <div className={styles.field}>
        <label className={styles.switchLabel}>
          <input
            type="checkbox"
            checked={config.sham ?? false}
            onChange={(e) => onChange({ sham: e.target.checked })}
          />
          <span>{t("DEVICE_SHAM")}</span>
          <span className={styles.switchState}>
            {config.sham ? t("DEVICE_SHAM_ON") : t("DEVICE_SHAM_OFF")}
          </span>
        </label>
      </div>

      <div className={styles.field}>
        <label className={styles.label}>{t("DEVICE_NOTES")}</label>
        <textarea
          className={styles.textarea}
          rows={3}
          value={config.notes ?? ""}
          onChange={(e) => onChange({ notes: e.target.value })}
        />
      </div>
    </>
  );
}

export default StimulationPanel;
